import * as Dialog from '@radix-ui/react-dialog';
import { CloseIcon } from '@/components/icons';
import { cn } from '@/utils/cn';
import { useBaseModal } from './base-modal.hook';
import { baseModalStyles } from './base-modal.styles';
import { BASE_MODAL } from './utils';
import { SHARED_TEST_IDS } from '@/utils';
import type {
	TBaseModalBodyConfig,
	TBaseModalConfig,
	TBaseModalFooterConfig
} from './base-modal.types';

const BaseModal = ({
	open,
	onOpenChange,
	title,
	titleId,
	description,
	size = 'md',
	height = 'auto',
	hideCloseButton = false,
	preventClose = false,
	headerSlot,
	className,
	dataTestId = SHARED_TEST_IDS.BASE_MODAL,
	children
}: TBaseModalConfig) => {
	const { handleInteractOutside, handleEscapeKeyDown, descriptionId } = useBaseModal({
		preventClose,
		titleId,
		description
	});

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className={baseModalStyles.overlay} />
				<Dialog.Content
					className={cn(baseModalStyles.content(size, height), className)}
					onInteractOutside={handleInteractOutside}
					onEscapeKeyDown={handleEscapeKeyDown}
					aria-describedby={descriptionId}
					data-testid={dataTestId}
				>
					{headerSlot && (
						<Dialog.Title className={baseModalStyles.srOnlyTitle} id={titleId}>
							{title ?? ''}
						</Dialog.Title>
					)}

					{(title || headerSlot || !hideCloseButton) && (
						<div className={baseModalStyles.header}>
							{headerSlot ? (
								<div className={baseModalStyles.headerCustomSlot}>{headerSlot}</div>
							) : (
								<Dialog.Title className={baseModalStyles.title} id={titleId}>
									{title ?? ''}
								</Dialog.Title>
							)}
							{!hideCloseButton && (
								<Dialog.Close asChild>
									<button
										type="button"
										className={baseModalStyles.closeButton}
										aria-label={BASE_MODAL.CLOSE_LABEL}
										disabled={preventClose}
										data-testid={SHARED_TEST_IDS.BASE_MODAL_CLOSE_BUTTON}
									>
										<CloseIcon />
									</button>
								</Dialog.Close>
							)}
						</div>
					)}

					{description && (
						<Dialog.Description className={baseModalStyles.srOnlyDescription} id={descriptionId}>
							{description}
						</Dialog.Description>
					)}

					{children}
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

BaseModal.displayName = 'BaseModal';

const BaseModalBody = ({ children, className, scrollable = false }: TBaseModalBodyConfig) => (
	<div className={cn(baseModalStyles.body(scrollable), className)}>{children}</div>
);

BaseModalBody.displayName = 'BaseModalBody';

const BaseModalFooter = ({ children, className }: TBaseModalFooterConfig) => (
	<div className={cn(baseModalStyles.footer, className)} data-testid={SHARED_TEST_IDS.BASE_MODAL_FOOTER}>
		{children}
	</div>
);

BaseModalFooter.displayName = 'BaseModalFooter';

export { BaseModal, BaseModalBody, BaseModalFooter };
